import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { Loader2 } from 'lucide-react';

export function BillingPlans() {
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [tokenCredit, setTokenCredit] = useState<number | ''>('');
  const [price, setPrice] = useState<number | ''>('');

  const loadPlans = async () => {
    try {
      setLoading(true);
      const res = await api.getBillingPlans();
      setPlans(res);
    } catch (e: any) {
      alert("Error loading plans: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const handleSave = async () => {
    if (!id || !name || tokenCredit === '' || price === '') {
      alert("Please fill all fields");
      return;
    }
    try {
      await api.upsertBillingPlan({ id, name, token_credit: Number(tokenCredit), price: Number(price) });
      setId('');
      setName('');
      setTokenCredit('');
      setPrice('');
      await loadPlans();
    } catch (e: any) {
      alert("Error saving plan: " + e.message);
    }
  };

  const handleEdit = (plan: any) => {
    setId(plan.id);
    setName(plan.name);
    setTokenCredit(plan.token_credit);
    setPrice(plan.price);
  };

  const handleDelete = async (planId: string) => {
    if (!window.confirm(`Are you sure you want to delete plan ${planId}?`)) return;
    try {
      await api.deleteBillingPlan(planId);
      await loadPlans();
    } catch (e: any) {
      alert("Error deleting plan: " + e.message);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', color: '#fff' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 8px 0' }}>Billing Plans</h1>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>Manage credit packages</p>
      </div>

      <div style={{ 
        background: 'var(--bg-elevated)', 
        border: '1px solid var(--border-color)', 
        borderRadius: '8px', 
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '14px', fontWeight: '600' }}>Create / Update Plan</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '8px', color: 'var(--text-secondary)' }}>Plan ID</label>
            <input 
              className="input-field"
              type="text" 
              placeholder="e.g. pack_1m"
              value={id}
              onChange={e => setId(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '1.5', minWidth: '200px' }}>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '8px', color: 'var(--text-secondary)' }}>Name</label>
            <input 
              className="input-field"
              type="text" 
              placeholder="e.g. 1M Tokens"
              value={name}
              onChange={e => setName(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '1', minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '8px', color: 'var(--text-secondary)' }}>Token Credit</label>
            <input 
              className="input-field"
              type="number" 
              placeholder="1000000"
              value={tokenCredit}
              onChange={e => setTokenCredit(e.target.value ? Number(e.target.value) : '')}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '1', minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '8px', color: 'var(--text-secondary)' }}>Price (USD)</label>
            <input 
              className="input-field"
              type="number" 
              placeholder="0.00"
              value={price}
              onChange={e => setPrice(e.target.value ? Number(e.target.value) : '')}
              style={{ width: '100%' }}
            />
          </div>
          <button 
            className="btn btn--primary" 
            onClick={handleSave}
            style={{ 
              height: '38px', 
              background: 'linear-gradient(135deg, #6366f1, #06b6d4)', 
              border: 'none',
              boxShadow: '0 0 10px rgba(99, 102, 241, 0.4)'
            }}
          >
            Save Plan
          </button>
        </div>
      </div>

      <div style={{ 
        background: 'var(--bg-elevated)', 
        border: '1px solid var(--border-color)', 
        borderRadius: '8px', 
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Loader2 className="spin" size={24} style={{ opacity: 0.5 }} />
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.02)', color: 'var(--text-muted)' }}>
                <th style={{ padding: '16px 24px', fontWeight: '500', borderBottom: '1px solid var(--border-color)' }}>ID</th>
                <th style={{ padding: '16px 24px', fontWeight: '500', borderBottom: '1px solid var(--border-color)' }}>NAME</th>
                <th style={{ padding: '16px 24px', fontWeight: '500', borderBottom: '1px solid var(--border-color)' }}>CREDIT</th>
                <th style={{ padding: '16px 24px', fontWeight: '500', borderBottom: '1px solid var(--border-color)' }}>PRICE</th>
                <th style={{ padding: '16px 24px', fontWeight: '500', borderBottom: '1px solid var(--border-color)' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {plans.map(plan => (
                <tr key={plan.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '16px 24px', fontWeight: '600' }}>{plan.id}</td>
                  <td style={{ padding: '16px 24px', fontWeight: '600' }}>{plan.name}</td>
                  <td style={{ padding: '16px 24px', fontWeight: '600' }}>
                    {Number(plan.token_credit).toLocaleString()}
                  </td>
                  <td style={{ padding: '16px 24px', fontWeight: '600' }}>
                    ${Number(plan.price).toFixed(2)}
                  </td>
                  <td style={{ padding: '16px 24px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        className="btn" 
                        style={{ 
                          background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.4)', 
                          color: '#67e8f9', height: '32px', padding: '0 12px', borderRadius: '6px', 
                          fontSize: '12px', fontWeight: '600', boxShadow: '0 0 10px rgba(6,182,212,0.2)',
                          textShadow: '0 0 5px rgba(103,232,249,0.5)', transition: 'all 0.2s'
                        }}
                        onClick={() => handleEdit(plan)}
                        onMouseOver={e => e.currentTarget.style.boxShadow = '0 0 15px rgba(6,182,212,0.5)'}
                        onMouseOut={e => e.currentTarget.style.boxShadow = '0 0 10px rgba(6,182,212,0.2)'}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn" 
                        style={{ 
                          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', 
                          color: '#fca5a5', height: '32px', padding: '0 12px', borderRadius: '6px', 
                          fontSize: '12px', fontWeight: '600', boxShadow: '0 0 10px rgba(239,68,68,0.2)',
                          textShadow: '0 0 5px rgba(252,165,165,0.5)', transition: 'all 0.2s'
                        }}
                        onClick={() => handleDelete(plan.id)}
                        onMouseOver={e => e.currentTarget.style.boxShadow = '0 0 15px rgba(239,68,68,0.5)'}
                        onMouseOut={e => e.currentTarget.style.boxShadow = '0 0 10px rgba(239,68,68,0.2)'}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {plans.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    No plans found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
